import type { Contact } from '../types/contact.types';

type UnknownRecord = Record<string, unknown>;

const DELIVERY_HEALTH_KEYS = [
  'ealertsDeliveryHealth',
  'ealertDeliveryHealth',
  'ealertsHealth',
  'ealertHealth',
  'ealertsHealthStatus',
  'ealertHealthStatus',
  'ealerts_delivery_health',
  'ealert_delivery_health',
  'ealerts_health',
  'ealert_health',
  'ealerts_health_status',
  'ealert_health_status',
] as const;

const DELIVERY_STATUS_KEYS = [
  'ealertsDeliveryStatus',
  'ealertDeliveryStatus',
  'ealertsLastDeliveryStatus',
  'ealertLastDeliveryStatus',
  'ealertsStatus',
  'ealertStatus',
  'ealerts_delivery_status',
  'ealert_delivery_status',
  'ealerts_last_delivery_status',
  'ealert_last_delivery_status',
  'ealerts_status',
  'ealert_status',
] as const;

const LAST_DELIVERY_AT_KEYS = [
  'ealertsLastDeliveredAt',
  'ealertLastDeliveredAt',
  'ealertsLastDeliveryAt',
  'ealertLastDeliveryAt',
  'ealerts_last_delivered_at',
  'ealert_last_delivered_at',
  'ealerts_last_delivery_at',
  'ealert_last_delivery_at',
] as const;

function parseJsonRecord(value: unknown): UnknownRecord | null {
  if (!value) return null;
  if (typeof value === 'object' && !Array.isArray(value)) return value as UnknownRecord;
  if (typeof value !== 'string') return null;

  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as UnknownRecord;
    }
  } catch {
    return null;
  }

  return null;
}

function getFirstString(source: UnknownRecord | null | undefined, keys: readonly string[]): string | undefined {
  if (!source) return undefined;

  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }

  return undefined;
}

function getEAlertSources(contact: Partial<Contact> & UnknownRecord): UnknownRecord[] {
  const sources: UnknownRecord[] = [contact];
  const metadata = parseJsonRecord(contact.metadata);
  const customFields = parseJsonRecord(contact.customFields);

  if (metadata) sources.push(metadata);
  if (customFields) sources.push(customFields);

  return sources;
}

export interface EAlertStatusSummary {
  isRegistered: boolean;
  registrationLabel: string;
  deliveryHealth: string | undefined;
  deliveryStatus: string | undefined;
  lastDeliveryAt: string | undefined;
  deliverySummary: string | undefined;
}

export function getEAlertStatusSummary(contact: (Partial<Contact> & UnknownRecord) | null | undefined): EAlertStatusSummary {
  const isRegistered = !!contact?.has_ealerts_registered;

  if (!contact) {
    return {
      isRegistered: false,
      registrationLabel: 'Setup',
      deliveryHealth: undefined,
      deliveryStatus: undefined,
      lastDeliveryAt: undefined,
      deliverySummary: undefined,
    };
  }

  const sources = getEAlertSources(contact);
  const deliveryHealth = sources
    .map((source) => getFirstString(source, DELIVERY_HEALTH_KEYS))
    .find(Boolean);
  const deliveryStatus = sources
    .map((source) => getFirstString(source, DELIVERY_STATUS_KEYS))
    .find(Boolean);
  const lastDeliveryAt = sources
    .map((source) => getFirstString(source, LAST_DELIVERY_AT_KEYS))
    .find(Boolean);

  const registrationLabel = isRegistered ? 'Registered' : 'Setup';
  const deliverySummary = isRegistered
    ? deliveryHealth || deliveryStatus || 'Delivery status unavailable'
    : undefined;

  return {
    isRegistered,
    registrationLabel,
    deliveryHealth,
    deliveryStatus,
    lastDeliveryAt,
    deliverySummary,
  };
}
